import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/components/ui/use-toast';
import BlockJudgesPicker from './BlockJudgesPicker';
import { LeadCardEditor, type LeadCardDraft } from '../../components/competition/LeadCardEditor';
import { LeadCardSummary } from '../../components/competition/LeadCardSummary';
import {
  leadStyleLabelKey,
  permittedLeadStyles,
  type LeadClassStyles,
} from '../../components/competition/lead-climb-style';
import { matchesFilter } from '../scoreboard-filter';
import { errorCodeOf } from '../../services/api-error';
import { competitionRoundsService } from '../../services/competition-rounds';
import {
  roundScoresService,
  type RoundScore,
  type RoundScoreClimb,
  type ScoreJudge,
} from '../../services/round-scores';

/** Which entrant's card on which route is open for editing. */
interface OpenCell {
  scoreId: string;
  roundClimbId: string;
}

interface RoundRoutesTableProps {
  competitionSlug: string;
  roundId: string;
  scores: RoundScore[];
  climbs: RoundScoreClimb[];
  /** The competition's judges, for handing a judge-only route to some of them. */
  judges: ScoreJudge[];
  /** The open class: a route belongs to the class that climbs it. */
  categoryId: string;
  /**
   * How each class climbs the round. Put together with a route's own forcing to
   * decide what a card's switch offers (`permittedLeadStyles`).
   */
  classStyles?: LeadClassStyles;
  /** What was typed into the filter box. */
  filter: string;
  /** Whether the viewer may say which routes need a judge. */
  canSetBlocks: boolean;
  /** Whether the round still takes cards at all. False once it is closed. */
  canScore: boolean;
  /** One entrant's card as the server saved it. */
  onScoreSaved: (score: RoundScore) => void;
  /** The server having said the round is closed, so the page can agree. */
  onRoundClosed?: () => void;
  /** One route as the server left it — its judge-only flag, its judges. */
  onClimbChanged: (climb: RoundScoreClimb) => void;
}

/** Names one cell of the table — an entrant's card on one route. */
const cellKey = (roundClimbId: string, scoreId: string) => `${roundClimbId}:${scoreId}`;

/**
 * A lead round read by route: one `<tbody>` per route, its name and its holds
 * spanning the whole field of people climbing it.
 *
 * The same table `RoundBlocksTable` is for a boulder round, and the difference is
 * the only one there is between the two disciplines: a boulder card is pressed in
 * three buttons as the goes happen, a lead card is two numbers typed once a
 * climber comes off the wall. So there is no live surface here to press — **one
 * row opens at a time** and carries the whole card, which is also what keeps fifty
 * entrants from being fifty sets of inputs on screen.
 *
 * Everything around it is unchanged: which routes need a judge and who stands at
 * them is set here by whoever runs the round, the server's `can_score` is what
 * says whether a card may be written, and a closed round takes nothing at all.
 */
export default function RoundRoutesTable({
  competitionSlug,
  roundId,
  scores,
  climbs,
  judges,
  categoryId,
  classStyles,
  filter,
  canSetBlocks,
  canScore,
  onScoreSaved,
  onClimbChanged,
  onRoundClosed,
}: RoundRoutesTableProps) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [open, setOpen] = useState<OpenCell | null>(null);
  /** The one cell with a save in flight, so only its own buttons go quiet. */
  const [busyCell, setBusyCell] = useState<string | null>(null);
  /** The one route whose judge-only flag is being saved. */
  const [busyRoute, setBusyRoute] = useState<string | null>(null);

  /**
   * Writes one card. Sent as the card should end up reading — the save is a
   * replacement, like the boulder table's — so an empty holds field takes the
   * card off rather than leaving a half-written one.
   */
  const writeCard = async (scoreId: string, roundClimbId: string, draft: LeadCardDraft) => {
    if (!competitionSlug || !roundId || busyCell || !canScore) return;

    setBusyCell(cellKey(roundClimbId, scoreId));
    try {
      const saved = await roundScoresService.save(competitionSlug, roundId, scoreId, [
        {
          round_climb_id: roundClimbId,
          holds_count: draft.holds,
          time_seconds: draft.timeSeconds,
          climbed_as: draft.style ?? null,
        },
      ]);
      onScoreSaved(saved);
      setOpen(null);
    } catch (error) {
      toast({
        title: t('gymAdmin.roundScores.saveError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
      if (errorCodeOf(error) === 'round_closed') onRoundClosed?.();
    } finally {
      setBusyCell(null);
    }
  };

  const clearCard = (scoreId: string, roundClimbId: string) =>
    writeCard(scoreId, roundClimbId, { holds: '', timeSeconds: null, style: null });

  /**
   * Marks a route as one the athletes cannot score for themselves — or lets them
   * again. Same flag, same meaning as on a block.
   */
  const setJudgeOnly = async (route: RoundScoreClimb, requiresJudge: boolean) => {
    if (!competitionSlug || !roundId || busyRoute) return;

    setBusyRoute(route.id);
    try {
      const saved = await competitionRoundsService.updateRoundClimb(
        competitionSlug,
        roundId,
        route.id,
        { requires_judge: requiresJudge }
      );
      onClimbChanged({ ...route, requires_judge: saved.requires_judge });
    } catch (error) {
      toast({
        title: t('gymAdmin.roundBlocks.judgeOnlySaveError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setBusyRoute(null);
    }
  };

  /** Hands a judge-only route to some of the judges; an empty list to all. */
  const assignJudges = async (route: RoundScoreClimb, judgeIds: string[]) => {
    if (!competitionSlug || !roundId) return;

    try {
      const saved = await competitionRoundsService.updateRoundClimb(
        competitionSlug,
        roundId,
        route.id,
        { judge_ids: judgeIds }
      );
      onClimbChanged({ ...route, judge_ids: saved.judge_ids ?? judgeIds });
    } catch (error) {
      toast({
        title: t('gymAdmin.roundBlocks.judges.saveError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
      throw error;
    }
  };

  const routes = useMemo(
    () =>
      climbs
        .filter((climb) => climb.category_id === null || climb.category_id === categoryId)
        .filter((climb) => canSetBlocks || climb.can_score !== false)
        .sort((a, b) => a.position - b.position),
    [climbs, categoryId, canSetBlocks]
  );

  const entrants = useMemo(
    () =>
      scores
        .filter((score) => score.category_id === categoryId)
        .sort((a, b) =>
          `${a.surname ?? ''} ${a.name ?? ''}`.localeCompare(
            `${b.surname ?? ''} ${b.name ?? ''}`,
            undefined,
            { sensitivity: 'base' }
          )
        ),
    [scores, categoryId]
  );

  const shownGroups = useMemo(
    () =>
      routes
        .map((route) => ({
          route,
          rows: matchesFilter(filter, route.name)
            ? entrants
            : entrants.filter((score) =>
                matchesFilter(filter, score.name, score.surname, score.full_name, score.team_name)
              ),
        }))
        .filter((group) => group.rows.length > 0),
    [routes, entrants, filter]
  );

  if (routes.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        {t('gymAdmin.roundBlocks.noRoutes')}
      </p>
    );
  }

  if (entrants.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        {t('gymAdmin.roundScores.empty')}
      </p>
    );
  }

  if (shownGroups.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        {t('gymAdmin.roundScores.noMatch')}
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b text-xs text-muted-foreground">
          <tr>
            <th className="px-3 py-2 text-left font-medium">{t('leadRounds.route')}</th>
            <th className="px-3 py-2 text-left font-medium">{t('gymAdmin.roundScores.athlete')}</th>
            <th className="px-3 py-2 text-left font-medium">{t('leadRounds.card')}</th>
            {/* A column of inputs is no use on paper. */}
            <th className="px-3 py-2 text-left font-medium print:hidden">
              {t('gymAdmin.roundBlocks.record')}
            </th>
          </tr>
        </thead>

        {/* A tbody per route, which is what lets its name and its holds span the
            whole field: somebody at the wall reads down a column rather than
            hunting for a repeated label. */}
        {shownGroups.map(({ route, rows }, groupIndex) => (
          <tbody
            key={route.id}
            data-testid="route-group"
            className={cn(
              'border-b-4 border-b-foreground/40',
              groupIndex === shownGroups.length - 1 && 'border-b-0'
            )}
          >
            {rows.map((score, index) => {
              const card = score.cards.find(
                (candidate) => candidate.round_climb_id === route.id
              );
              const editing =
                open?.scoreId === score.id && open?.roundClimbId === route.id;
              const saving = busyCell === cellKey(route.id, score.id);
              const writable = canScore && card?.can_score !== false;

              return (
                <tr
                  key={`${route.id}-${score.id}`}
                  className="border-b last:border-0"
                  data-state={editing ? 'selected' : undefined}
                  data-testid="route-row"
                >
                  {index === 0 && (
                    <td
                      rowSpan={rows.length}
                      className="border-r px-3 py-2 align-top font-medium"
                      data-testid="route-name"
                    >
                      {route.name ?? t('gymAdmin.roundScores.unnamedClimb')}

                      {/* The holds on it — what every card in this field is read
                          against. A route with none is one the round would not
                          have started on, so it says so rather than staying
                          blank. */}
                      <span
                        className={cn(
                          'mt-0.5 block whitespace-nowrap text-xs font-normal',
                          route.holds_count == null ? 'text-destructive' : 'text-muted-foreground'
                        )}
                        data-testid="route-holds-count"
                      >
                        {route.holds_count == null
                          ? t('leadRounds.holdsMissing')
                          : t('leadRounds.holdsOnRoute', { count: route.holds_count })}
                      </span>

                      {/* A route taken out of the choice. Said here rather than
                          offered: the forcing is part of the round's setup and
                          is decided on its setup page. */}
                      {route.required_climb_style && (
                        <span
                          className="mt-0.5 block whitespace-nowrap text-xs font-normal text-muted-foreground"
                          data-testid="route-forced-style"
                        >
                          {t('leadRounds.style.forced', {
                            style: t(leadStyleLabelKey(route.required_climb_style)),
                          })}
                        </span>
                      )}

                      {canSetBlocks ? (
                        <>
                          <label
                            className="mt-1.5 flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-xs font-normal text-muted-foreground print:hidden"
                            title={t('gymAdmin.roundBlocks.judgeOnlyHint')}
                          >
                            <Checkbox
                              checked={route.requires_judge}
                              disabled={busyRoute === route.id}
                              onCheckedChange={(checked) => setJudgeOnly(route, checked === true)}
                              aria-label={t('gymAdmin.roundBlocks.judgeOnly')}
                              data-testid="route-judge-toggle"
                            />
                            {t('gymAdmin.roundBlocks.judgeOnly')}
                          </label>
                          {route.requires_judge && (
                            <div className="print:hidden">
                              <BlockJudgesPicker
                                judges={judges}
                                judgeIds={route.judge_ids ?? []}
                                onChange={(ids) => assignJudges(route, ids)}
                              />
                            </div>
                          )}
                        </>
                      ) : (
                        route.requires_judge && (
                          <span
                            className="ml-1 text-xs font-normal text-muted-foreground"
                            data-testid="route-judge-label"
                          >
                            {t('gymAdmin.roundBlocks.judgeLabel')}
                          </span>
                        )
                      )}
                    </td>
                  )}

                  <td className="px-3 py-2">
                    {[score.name, score.surname].filter(Boolean).join(' ') || score.full_name}
                  </td>

                  <td className="px-3 py-2">
                    <LeadCardSummary
                      holds={card?.holds_count}
                      timeSeconds={card?.time_seconds}
                      formattedTime={card?.formatted_time}
                      topped={card?.topped}
                      climbedAs={card?.climbed_as}
                    />
                  </td>

                  <td className="px-3 py-2 print:hidden">
                    {/* One row at a time: a lead card is typed, not pressed, and
                        fifty entrants' worth of inputs on screen at once would be
                        a form nobody can read. */}
                    {editing ? (
                      <LeadCardEditor
                        holds={card?.holds_count ?? null}
                        timeSeconds={card?.time_seconds ?? null}
                        climbedAs={card?.climbed_as}
                        styles={permittedLeadStyles(route, score.category_id, classStyles)}
                        maxHolds={route.holds_count}
                        saving={saving}
                        disabled={!writable}
                        onSave={(draft) => writeCard(score.id, route.id, draft)}
                        onClear={() => clearCard(score.id, route.id)}
                        data-testid="route-card-editor"
                      />
                    ) : (
                      writable &&
                      card && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 gap-1.5 text-xs"
                          onClick={() => setOpen({ scoreId: score.id, roundClimbId: route.id })}
                          data-testid="open-route-card"
                        >
                          <Pencil className="size-3" aria-hidden />
                          {card.holds_count == null
                            ? t('leadRounds.recordCard')
                            : t('leadRounds.editCard')}
                        </Button>
                      )
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        ))}
      </table>
    </div>
  );
}
